import { useState } from "react";
import { useT } from "@/i18n/locale-context";

export type GdprConsent = {
  storePhotos: boolean;
  improveAi: boolean;
  acceptedAt: string;
  version: 1;
};

const STORAGE_KEY = "symmetria.gdpr.consent.v1";

export function getStoredConsent(): GdprConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as GdprConsent) : null;
  } catch {
    return null;
  }
}

export function clearStoredConsent() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function GdprConsentModal({
  onAccept,
  onCancel,
}: {
  onAccept: (c: GdprConsent) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [storePhotos, setStorePhotos] = useState(false);
  const [improveAi, setImproveAi] = useState(false);
  const [analyseAccepted, setAnalyseAccepted] = useState(false);

  function submit() {
    const consent: GdprConsent = {
      storePhotos,
      improveAi,
      acceptedAt: new Date().toISOString(),
      version: 1,
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {}
    onAccept(consent);
  }

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-background border border-foreground/10 rounded-3xl max-w-2xl w-full p-8 md:p-10 shadow-2xl max-h-[90vh] overflow-y-auto">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("gdpr.label")}</span>
        <h2 className="font-display font-bold text-3xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("gdpr.title")}
        </h2>
        <p className="text-sm text-muted-foreground mb-6">{t("gdpr.intro")}</p>

        <div className="space-y-4">
          <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
            <input type="checkbox" checked={analyseAccepted} onChange={(e) => setAnalyseAccepted(e.target.checked)} className="mt-1 accent-accent" />
            <div>
              <p className="font-semibold text-sm mb-1">
                {t("gdpr.analyse.title")} <span className="text-accent">{t("gdpr.analyse.required")}</span>
              </p>
              <p className="text-xs text-muted-foreground">{t("gdpr.analyse.desc")}</p>
            </div>
          </label>

          <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
            <input type="checkbox" checked={storePhotos} onChange={(e) => setStorePhotos(e.target.checked)} className="mt-1 accent-accent" />
            <div>
              <p className="font-semibold text-sm mb-1">
                {t("gdpr.store.title")} <span className="text-muted-foreground">{t("gdpr.store.optional")}</span>
              </p>
              <p className="text-xs text-muted-foreground">{t("gdpr.store.desc")}</p>
            </div>
          </label>

          <label className="flex gap-3 p-4 rounded-2xl border border-foreground/10 cursor-pointer">
            <input type="checkbox" checked={improveAi} onChange={(e) => setImproveAi(e.target.checked)} className="mt-1 accent-accent" />
            <div>
              <p className="font-semibold text-sm mb-1">
                {t("gdpr.improve.title")} <span className="text-muted-foreground">{t("gdpr.improve.optional")}</span>
              </p>
              <p className="text-xs text-muted-foreground">{t("gdpr.improve.desc")}</p>
            </div>
          </label>
        </div>

        <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">{t("gdpr.legal")}</p>

        <div className="flex flex-col-reverse md:flex-row gap-3 mt-8">
          <button onClick={onCancel} className="md:flex-1 px-5 py-3 rounded-full border border-foreground/15 text-sm font-semibold hover:bg-muted">
            {t("gdpr.cancel")}
          </button>
          <button onClick={submit} disabled={!analyseAccepted} className="md:flex-1 px-5 py-3 rounded-full bg-foreground text-background text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
            {t("gdpr.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
